import {
  Accordion,
  Button,
  Card,
  Column,
  Divider,
  Heading, Input, Row,
  Spacer, Text
} from '@oliasoft-open-source/react-ui-library';
import translations from 'client/internationalization/translation-map.json';
import { RootState } from 'client/store/configureStore';
import { getArchives } from 'client/store/entities/hierarchy/archive/archive';
import { runQuery, upsertQuery } from 'client/store/entities/query/query';
import { EntityType } from 'common/enums/entity-enums';
import {
  ICondition, IQuery
} from 'common/interfaces/query';
import { isEmpty, isEqual } from 'es-toolkit/compat';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConnectedProps, connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useImmer } from 'use-immer';
import { QueryResults } from '../query-result/query-results';
import {
  allConditionsFilled,
  defaultCondition,
  defaultQuery,
  emptyCondition,
  getConditionDataTypeKey,
  replaceConditionDataType
} from '../query-utils';
import { QueryRow } from './query-query-row';

interface IQueryFormProps extends PropsFromRedux {
  selectedQuery?: IQuery;
  setSelectedQuery: (selectedQuery: IQuery) => void;
  isAnalyticPage?: boolean;
}

const QueryForm = (
  {
    archives,
    getArchives,
    runQuery,
    upsertQuery,
    selectedQuery,
    setSelectedQuery,
    isAnalyticPage = false,
    queryResult,
    isRunning,
  }: IQueryFormProps,
) => {
  const { t } = useTranslation();
  const { companyId } = useParams();
  const [query, setQuery] = useState<IQuery>(selectedQuery ?? defaultQuery({ companyId: companyId! }) as IQuery);
  const [queryName, setQueryName] = useState('');
  const [conditions, setConditions] = useImmer<ICondition[]>([defaultCondition]);

  // Keeps the Data type selection in sync with the condition's entity parameter
  const syncConditionDataType = (index: number, updatedCondition?: ICondition) => {
    const oldKey = getConditionDataTypeKey(conditions[index]);
    const newKey = updatedCondition && getConditionDataTypeKey(updatedCondition);
    const otherConditions = conditions.filter((_condition, conditionIndex) => conditionIndex !== index);
    setQuery(prevQuery => ({
      ...prevQuery,
      dataType: replaceConditionDataType(prevQuery.dataType, oldKey, newKey, otherConditions),
    }));
  };

  const updateCondition = (index: number, changes: Partial<ICondition>) => {
    const updatedCondition = { ...conditions[index], ...changes };
    setConditions((draft) => {
      draft[index] = updatedCondition;
    });
    // Only entity type / parameter changes affect the Data type column
    if (getConditionDataTypeKey(updatedCondition) !== getConditionDataTypeKey(conditions[index])) {
      syncConditionDataType(index, updatedCondition);
    }
  };
  const setDataType = (dataType: { label: string, value: string, entityType: EntityType }[]) => {
    const updatedQuery = {
      ...query,
      dataType: dataType.map(dt => dt.value),
    };
    setQuery(updatedQuery);
  };

  const setHierarchyData = (data: { label: string, value: string }[]) => {
    const updatedQuery = {
      ...query,
      resultConfig: { ...query.resultConfig, hierarchy: data.map(item => item.value) },
    };
    setQuery(updatedQuery);
  };

  useEffect(() => {
    if (isEmpty(archives)) {
      getArchives();
    }
  });

  useEffect(() => {
    if (!companyId) {
      return;
    }
    if (selectedQuery) {
      setQuery(selectedQuery);
      setQueryName(selectedQuery.queryName);
      setConditions(selectedQuery.conditions);
      return;
    }
    setQuery(defaultQuery({ companyId }) as IQuery);
    setConditions([defaultCondition]);
    setQueryName('');
  }, [selectedQuery, companyId]);

  const getEditedQuery = () => {
    const customQuery: IQuery = {
      ...selectedQuery,
      ...query,
      conditions,
      queryName,
      companyid: companyId!,
    };
    return customQuery;
  };

  const onQuerySubmit = () => {
    const customQuery = getEditedQuery();
    setSelectedQuery(customQuery);
    runQuery(customQuery);
  };

  const onSaveQuery = () => {
    const customQuery = getEditedQuery();
    upsertQuery(customQuery);
    if (customQuery.queryid) {
      setSelectedQuery(customQuery);
    }
  };

  const handleDeleteCondition = (index: number) => {
    syncConditionDataType(index);
    setConditions(conditions.filter((_cond, ind) => ind !== index));
  };
  const handleAddCondition = () => {
    //@ts-ignore
    setConditions([...conditions, emptyCondition]);
  };

  const isQueryEditDisabled = !allConditionsFilled([...conditions, { name: queryName, archiveid: query.archiveid }]) || isEqual(selectedQuery, getEditedQuery());

  return (
    <>
      <Card bordered={false}>
        <Row>
          <Column width={200}>
            <Text bold>{t(translations.query_queryName)}</Text>
            <Spacer height="var(--padding-xxs)" />
            <Input value={queryName} onChange={(event) => setQueryName(event.target.value)} />
          </Column>
          {/* <Column width={200}>
            <Text bold>{t(translations.archive)}</Text>
            <Spacer height="var(--padding-xxs)" />
            <Select value={query?.archiveid} options={archiveOptions} onChange={(event) => { setQuery({ ...query, archiveid: event.target.value }); }} disabled={!archives} />
          </Column> */}
        </Row>
      </Card>
      <Divider />
      <Card bordered={false}>
        <Accordion
          expanded
          managed
          heading={<Heading>{t(translations.query_condition)}</Heading>}>
          <QueryRow
            handleDeleteCondition={handleDeleteCondition}
            updateCondition={updateCondition}
            conditions={conditions} />
          {!isAnalyticPage && (
            <Button
              colored
              label={t(translations.query_runQuery)}
              onClick={onQuerySubmit}
              width={110}
            />
          )}
          <Spacer width="var(--padding)" />
          <Button
            label="Add Condition"
            onClick={handleAddCondition}
            width={110}
          />
          <Spacer width="var(--padding)" />
          {!isAnalyticPage && (
            <Button
              label={selectedQuery ? t(translations.query_updateQuery) : t(translations.query_saveQuery)}
              onClick={onSaveQuery}
              width={110}
              disabled={isQueryEditDisabled}
            />
          )}
        </Accordion>
      </Card>
      <Divider />
      <Card bordered={false}>
        {queryResult && <QueryResults
          query={query}
          queryResult={queryResult}
          isRunning={isRunning}
          setQuery={setQuery}
          setHierarchyData={setHierarchyData}
          setDataType={setDataType} />}
      </Card>
    </>
  );
};

const mapStateToProps = ({ entities }: RootState) => ({
  archives: entities?.archive.archives,
  queryResult: entities?.query.result,
  isRunning: entities?.query.isRunning,
});

const mapDispatchToProps = {
  getArchives,
  runQuery,
  upsertQuery,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = connector(QueryForm);

export { Container as QueryForm };
